"use client";
import { MapInfoCard, Maps, MultiSelectFilter } from "@/Components";
import { DRIVER_STATUS, DriverStatus, MARKER_IMAGES } from "@/Constants";
import { useActiveCapability } from "@/hooks/capabilitiesHook";
import { useDriversLocations } from "@/hooks/locations";
import { Name } from "@/types";
import { useRetrieveTitle } from "@/Utils/helpers";
import { orderBy } from "lodash";
import get from "lodash/get";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { MapRef, Marker, Popup } from "react-map-gl";
import { Loader } from "../../app/loading";
import Img from "@/app/Img";
import OrderView from "@/Components/Dashboard/OrderView";
import TableView from "./TableView";
import { getTitleLanguage } from "@/Utils/orderHelpers";
import { StaticImageData } from "next/image";


type FitBoundsOptions = {
  padding?: number;
  duration?: number;
};

const calculateBounds = (
  latitudes: number[] = [],
  longitudes: number[] = []
): [[number, number], [number, number]] | null => {
  // ✅ Validate input
  if (
    !latitudes.length ||
    !longitudes.length ||
    latitudes.length !== longitudes.length
  ) {
    console.warn("Invalid coordinates for bounds calculation");
    return null;
  }

  // ✅ Filter invalid values
  const validPoints = latitudes
    .map((lat, i) => ({
      lat,
      lng: longitudes[i],
    }))
    .filter(
      (p) =>
        typeof p.lat === "number" &&
        typeof p.lng === "number" &&
        !isNaN(p.lat) &&
        !isNaN(p.lng)
    );

  if (!validPoints.length) return null;

  const lats = validPoints.map((p) => p.lat);
  const lngs = validPoints.map((p) => p.lng);

  return [
    [Math.min(...lngs), Math.min(...lats)], // SW
    [Math.max(...lngs), Math.max(...lats)], // NE
  ];
};

const fitBoundsToMap = (
  mapRef: React.RefObject<MapRef>,
  bounds: [[number, number], [number, number]] | null,
  options: FitBoundsOptions = { padding: 100, duration: 500 }
) => {
  if (!mapRef?.current || !bounds) return;

  try {
    mapRef.current.fitBounds(bounds, options);
  } catch (error) {
    console.error("Error fitting bounds:", error);
  }
};

const Dashboard = () => {
  const activeTab = useSearchParams().get("tab");
  const { getTitle, language } = useRetrieveTitle();
  const mapDimension = { width: "100%", height: "100%" };
  const pathname = usePathname();
  const router = useRouter();
  const mapRef: any = useRef<MapRef>();
  const [viewport] = useState<any>({
    width: 2000,
    height: 2000,
    latitude: 0,
    longitude: 0,
    zoom: 1,
  });
  const [selectedMarker, setSelectedMarker] = useState<any>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [activeStatus, setActiveStatus] = useState<string[]>([]);
  const [finalCapabilities, setFinalCapabilities] = useState<any[]>([]);
  const { data: capabilitiesRes, isFetching: isCapabilityFetched } = useActiveCapability();
  const { data: locationRes, isLoading, isFetching } = useDriversLocations();

  const liveLocationData = get(locationRes, "data.features", []);

  const getFilteredAndSortedData = () => {
    let filtered = liveLocationData;

    // ✅ Filter by capabilities
    if (activeFilters.length) {
      filtered = filtered.filter((location: any) =>
        location?.properties?.capabilities?.some((id: string) =>
          activeFilters.includes(id)
        )
      );
    }

    // ✅ Filter by driver status
    if (activeStatus.length) {
      filtered = filtered.filter((location: any) =>
        activeStatus.includes(location?.properties?.driver_status)
      );
    }

    return orderBy(filtered, ["properties.driver_id"], ["desc"]);
  };

  const mapSourceData = getFilteredAndSortedData();

  useEffect(() => {
    if (!isCapabilityFetched && capabilitiesRes?.data) {
      setFinalCapabilities(capabilitiesRes.data);
    }
  }, [capabilitiesRes, isCapabilityFetched]);

  const capabilities = finalCapabilities;

  const capabilityOption = capabilities.map((cap) => ({
    label: getTitleLanguage(cap.label, language) || cap.label["en-us"] || "-",
    value: cap.id,
  }));

  const driverStatusOptions = useMemo(() => {
    return DRIVER_STATUS.map((status) => ({
      value: status.value,
      label: getTitle(status.value) || status.label
    }));
  }, [getTitle]);

  const getMarkerImage = (status?: string): StaticImageData => {
    return MARKER_IMAGES[status as DriverStatus] ?? MARKER_IMAGES.inactive;
  };

  const handleMarkerClick = async (event: any, data: any) => {
    event.originalEvent.stopImmediatePropagation();
    setSelectedMarker(data);
  };

  const letCoords: number[] =
    liveLocationData?.map((location: any) => location.properties.latitude) ||
    [];

  const longCoords: number[] =
    liveLocationData?.map((location: any) => location.properties.longitude) ||
    [];

  const prevCount = useRef(0);

  useEffect(() => {
    if (!liveLocationData.length) return;

    if (prevCount.current !== liveLocationData.length) {
      const bounds = calculateBounds(letCoords, longCoords);
      fitBoundsToMap(mapRef, bounds);

      prevCount.current = liveLocationData.length;
    }
  }, [liveLocationData]);

  return (
    <>
      {!locationRes ? (
        <Loader size="default" />
      ) : (
        <>
          {(activeTab === "map_view" || activeTab === "list_view") && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                paddingBottom: "16px",
              }}
            >
              <MultiSelectFilter
                options={capabilityOption}
                onChange={(val: any) => setActiveFilters(val)}
                placeholder={getTitle("filter_by_capabilities")}
              />
              <MultiSelectFilter
                options={driverStatusOptions}
                placeholder={getTitle("filter_by_driver_status")}
                onChange={(val: any) => setActiveStatus(val)}
              />
            </div>
          )}

          {activeTab === "map_view" && (
            <div className="mapContainer">
              <Maps
                mapRef={mapRef}
                viewport={viewport}
                mapDimension={mapDimension}
              >
                {liveLocationData && (
                  <>
                    {mapSourceData?.map((obj: any, index: number) => {
                      const { latitude, longitude, driver_status, driver_id } =
                        obj.properties;
                      return (
                        <Marker
                          anchor="bottom"
                          key={`${driver_id}-${latitude}-${longitude}`}
                          latitude={latitude}
                          longitude={longitude}
                          onClick={(e) => handleMarkerClick(e, obj)}
                        >
                          <Img
                            src={getMarkerImage(driver_status)}
                            width={23}
                            height={27}
                            alt="marker"
                          />
                        </Marker>
                      );
                    })}

                    <div>
                      {selectedMarker && (
                        <Popup
                          latitude={selectedMarker?.properties?.latitude || 0}
                          longitude={selectedMarker?.properties?.longitude || 0}
                          anchor="bottom"
                          offset={
                            (selectedMarker ? [0, -20] : undefined) as
                            | [number, number]
                            | undefined
                          }
                          onClose={() => setSelectedMarker(null)}
                        >
                          <MapInfoCard
                            data={selectedMarker}
                            capabilities={capabilities}
                          />
                        </Popup>
                      )}
                    </div>
                  </>
                )}
              </Maps>
            </div>
          )}
          {activeTab === "list_view" && (
            <TableView
              liveLocationData={liveLocationData}
              activeFilters={activeFilters}
              activeStatus={activeStatus}
              isLoading={isLoading}
            />
          )}
          {/* {activeTab === "order_view" && <OrderView />} */}
        </>
      )}
    </>
  );
};

export default Dashboard;
